/**
 * CR-6403: the Supplier Performance filter body, grouped into the four sections
 * from the supplied design — Date Range · Company · Supplier · Classification.
 *
 * Every control here is the SAME element bound to the SAME handler it had when
 * it lived inline in SupplierPerformanceFilters: only the grouping and order
 * change. The ticket is explicit that the diagram is AI-generated and may
 * misrepresent input types, and that the existing cascades must not regress, so
 * nothing about the inputs or their callbacks has been altered:
 *   - division → company site      (scopedDivisions / viewFilteredSites)
 *   - site → supplier              (supplierSites)
 *   - supplier country → supplier site
 *   - commodity group → commodity type (the onChange still resets the type)
 *
 * Extracted into its own file so the parent stays under the 500-line limit.
 */
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import SearchableSelect from '../../ui/SearchableSelect';
import type {
  Site, CompanyGroup, Warehouse, SupplierRatingThreshold, CommodityGroup, CommodityType,
} from '../../../lib/api';
import type { SupplierPerformanceFilterValues } from '../SupplierPerformanceFilters';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { bandRange } from '../../../pages/supplier-performance/utils';

const SECTION_TITLE = 'text-[11px] font-semibold uppercase tracking-wide text-foreground mb-2';
const GRID = 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4';

export interface FilterSectionsProps {
  localFilters: SupplierPerformanceFilterValues;
  setLocalFilters: React.Dispatch<React.SetStateAction<SupplierPerformanceFilterValues>>;
  updateFilter: <K extends keyof SupplierPerformanceFilterValues>(
    key: K, value: SupplierPerformanceFilterValues[K],
  ) => void;
  applyAndUpdate: <K extends keyof SupplierPerformanceFilterValues>(
    key: K, value: SupplierPerformanceFilterValues[K],
  ) => void;
  scopedDivisions: CompanyGroup[];
  isDivisionLocked: boolean;
  /** CR-6403: sets Division and drops a Company Site that is no longer in it. */
  /** `apply` = the pick was a clear (the x) and must reach the page at once. */
  onDivisionChange: (val: string, apply?: boolean) => void;
  viewFilteredSites: Site[];
  isSiteLocked: boolean;
  supplierGroups: CompanyGroup[];
  /**
   * CR-6403: the FULL supplier list. Used only to build the Supplier Country
   * options — deriving those from the narrowed list would collapse the country
   * dropdown to whichever country is already selected.
   */
  supplierSites: Array<Site & { company_name?: string }>;
  /**
   * CR-6403: the supplier list after the Company Site and Supplier Country
   * cascades have been applied. Drives the Supplier Site dropdown only.
   */
  supplierSiteOptions: Array<Site & { company_name?: string }>;
  warehouses: Warehouse[];
  commodityGroups: CommodityGroup[];
  commodityTypes: CommodityType[];
  ratingThresholds: SupplierRatingThreshold[];
  supplierTypes: ReadonlyArray<{ value: string; label: string }>;
  quickRanges: React.ReactNode;
}

export default function FilterSections({
  localFilters,
  setLocalFilters,
  updateFilter,
  applyAndUpdate,
  scopedDivisions,
  isDivisionLocked,
  onDivisionChange,
  viewFilteredSites,
  isSiteLocked,
  supplierGroups,
  supplierSites,
  supplierSiteOptions,
  warehouses,
  commodityGroups,
  commodityTypes,
  ratingThresholds,
  supplierTypes,
  quickRanges,
}: FilterSectionsProps) {
  const { warehouseModuleEnabled } = useMenuSettings();
  const lockedCell = (label: string) => (
    <div className="w-full h-9 px-3 py-2 border border-border rounded-md bg-muted/40 text-muted-foreground text-sm cursor-not-allowed flex items-center">
      {label}
    </div>
  );

  // CR-4802: distinct Company Country values across the view-scoped sites —
  // drives both the option list and whether the filter shows at all.
  const siteCountryValues = Array.from(
    new Set(viewFilteredSites.map((s) => s.country).filter(Boolean)),
  ).sort() as string[];

  return (
    <>
      {/* ---------------- Date Range ---------------- */}
      <div className="mb-5">
        <div className={SECTION_TITLE}>Date Range</div>
        {quickRanges}
        <div className={GRID}>
          <div className="space-y-1.5">
            <Label htmlFor="dateFrom" className="text-xs">From Date</Label>
            <Input
              id="dateFrom"
              type="date"
              value={localFilters.dateFrom}
              onChange={(e) => updateFilter('dateFrom', e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateTo" className="text-xs">To Date</Label>
            <Input
              id="dateTo"
              type="date"
              value={localFilters.dateTo}
              onChange={(e) => updateFilter('dateTo', e.target.value)}
              className="h-9"
            />
          </div>
        </div>
        {/* Data mode sits with the date controls per the design's DATE RANGE block.
            W3-T9: it applies on click like the dashboard's switch (and like the
            quick ranges here, CR-4783) — a Live-selected switch over Historical
            figures is exactly what the switch exists to prevent. */}
        <div className="flex items-center gap-2 mt-3">
          <span className="text-xs text-muted-foreground">Data</span>
          <div className="flex items-center gap-2 border rounded-md p-1">
            <Button
              variant={localFilters.dataMode === 'historical' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => applyAndUpdate('dataMode', 'historical')}
            >
              Historical
            </Button>
            <Button
              variant={localFilters.dataMode === 'live' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => applyAndUpdate('dataMode', 'live')}
            >
              Live
            </Button>
          </div>
        </div>
      </div>

      {/* ---------------- Company ---------------- */}
      <div className="mb-5">
        <div className={SECTION_TITLE}>Company</div>
        <div className={GRID}>
          {scopedDivisions.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="division" className="text-xs">Division</Label>
              {isDivisionLocked ? lockedCell(scopedDivisions[0]?.name || 'Selected division') : (
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Divisions' }, ...scopedDivisions.map((d) => ({ value: d.id.toString(), label: d.name }))]}
                  value={localFilters.division}
                  // CR-7264 rework: the x on a select is a clear, and a clear applies at once.
                  onChange={(val) => onDivisionChange(val || 'all', !val)}
                  placeholder="All Divisions"
                />
              )}
            </div>
          )}
          {viewFilteredSites.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="site" className="text-xs">Company Site</Label>
              {isSiteLocked ? lockedCell(viewFilteredSites[0]?.name || 'Selected site') : (
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Company Sites' }, ...viewFilteredSites.map((s) => ({ value: s.id.toString(), label: s.name }))]}
                  value={localFilters.site}
                  onChange={(val) => (val ? updateFilter('site', val) : applyAndUpdate('site', 'all'))}
                  placeholder="All Company Sites"
                />
              )}
            </div>
          )}
          {/* CR-4802: hidden when every accessible site shares one country —
              nothing for the filter to narrow. Mirrors the Dashboard gate. */}
          {siteCountryValues.length > 1 && (
          <div className="space-y-1.5">
            <Label htmlFor="siteCountry" className="text-xs">Company Country</Label>
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Countries' },
                ...siteCountryValues.map((c) => ({ value: c, label: c })),
              ]}
              value={localFilters.siteCountry}
              onChange={(val) => applyAndUpdate('siteCountry', val || 'all')}
              placeholder="All Countries"
            />
          </div>
          )}
          {/* CR-6312 rework: the warehouse filter renders only where the
              tenant has switched the warehouse module on (the admin-form
              toggle that previously gated nothing). Still not gated on
              warehouses.length (CR-4802): an empty list is a data state,
              not a feature state. */}
          {warehouseModuleEnabled && (
          <div className="space-y-1.5">
            <Label htmlFor="warehouse" className="text-xs">Warehouse</Label>
            <Select
              value={localFilters.warehouse}
              onValueChange={(value) => updateFilter('warehouse', value)}
            >
              <SelectTrigger id="warehouse" className="h-9">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={warehouse.id.toString()}>
                    {warehouse.name} ({warehouse.site_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}
        </div>
      </div>

      {/* ---------------- Supplier ---------------- */}
      <div className="mb-5">
        <div className={SECTION_TITLE}>Supplier</div>
        <div className={GRID}>
          <div className="space-y-1.5">
            <Label htmlFor="supplierGroup" className="text-xs">Supplier Group</Label>
            <SearchableSelect
              options={[{ value: 'all', label: 'All Supplier Groups' }, ...supplierGroups.map((g) => ({ value: g.id.toString(), label: g.name }))]}
              value={localFilters.supplierGroup}
              onChange={(val) => (val ? updateFilter('supplierGroup', val) : applyAndUpdate('supplierGroup', 'all'))}
              placeholder="All Supplier Groups"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplierSite" className="text-xs">Supplier Site</Label>
            <SearchableSelect
              // CR-6403: every option used to render as "Name (undefined)".
              // Neither path that populates supplierSites sets company_name —
              // the Helix branch stores raw Company records and the non-Helix
              // branch maps to {id, name} — so the parenthetical was always
              // undefined. Show it only when there is actually a company to
              // show, which is what the template intended.
              // CR-6403: supplierSiteOptions, not supplierSites — this list is
              // narrowed by the Company Site and Supplier Country cascades.
              options={[
                { value: 'all', label: 'All Supplier Sites' },
                ...supplierSiteOptions.map((s) => ({
                  value: s.id.toString(),
                  label: s.company_name ? `${s.name} (${s.company_name})` : s.name,
                })),
              ]}
              value={localFilters.supplierSite}
              onChange={(val) => (val ? updateFilter('supplierSite', val) : applyAndUpdate('supplierSite', 'all'))}
              placeholder="All Supplier Sites"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplierType" className="text-xs">Supplier Type</Label>
            <Select
              value={localFilters.supplierType}
              onValueChange={(value) => updateFilter('supplierType', value)}
            >
              <SelectTrigger id="supplierType" className="h-9">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                {supplierTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="supplierCountry" className="text-xs">Supplier Country</Label>
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Countries' },
                ...Array.from(new Set(supplierSites.map((s) => s.country).filter(Boolean)))
                  .sort()
                  .map((c) => ({ value: c as string, label: c as string })),
              ]}
              value={localFilters.supplierCountry}
              onChange={(val) => applyAndUpdate('supplierCountry', val || 'all')}
              placeholder="All Countries"
            />
          </div>
          <div className="space-y-2 pt-5">
            <div className="flex items-center gap-2">
              <Checkbox
                id="strategic"
                checked={localFilters.strategic}
                onCheckedChange={(checked) => updateFilter('strategic', checked as boolean)}
              />
              <Label htmlFor="strategic" className="text-xs cursor-pointer">Strategic</Label>
            </div>
            {/* CR-7264 rework: "Hide Child Companies" was removed here — it had
                no consumer anywhere (a placeholder since the panel was built),
                and a company has no parent to hide by. */}
          </div>
        </div>
      </div>

      {/* ---------------- Classification ---------------- */}
      <div className="mb-5">
        <div className={SECTION_TITLE}>Classification</div>
        <div className={GRID}>
          {/* CR-4802: hidden together when the tenant hasn't configured any
              commodity groups in Admin Setup — mirrors the same gate on the
              Dashboard and NCR filter panels. commodityGroups is the full
              tenant list, not narrowed by any current selection. */}
          {commodityGroups.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="commodityGroup" className="text-xs">Commodity Group</Label>
            <Select
              value={localFilters.commodityGroup}
              onValueChange={(value) => {
                updateFilter('commodityGroup', value);
                // Reset commodity type when group changes
                updateFilter('commodityType', 'all');
              }}
            >
              <SelectTrigger id="commodityGroup" className="h-9">
                <SelectValue placeholder="All Commodities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Commodities</SelectItem>
                {commodityGroups.map((group) => (
                  <SelectItem key={group.id} value={group.id.toString()}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          )}
          {commodityGroups.length > 0 && (
          <div className="space-y-1.5">
            <Label htmlFor="commodityType" className="text-xs">Commodity Type</Label>
            <Select
              value={localFilters.commodityType}
              onValueChange={(value) => updateFilter('commodityType', value)}
              disabled={localFilters.commodityGroup === 'all'}
            >
              <SelectTrigger id="commodityType" className="h-9">
                <SelectValue placeholder={localFilters.commodityGroup === 'all' ? 'Select group first' : 'All Types'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {commodityTypes
                  .filter((type) => localFilters.commodityGroup === 'all' || type.commodity_group.toString() === localFilters.commodityGroup)
                  .map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          )}
          {/* CR-7273: Order Type popover removed — the column it filtered is dormant. */}
          <div className="space-y-1.5">
            <Label htmlFor="rating" className="text-xs">Rating</Label>
            <Select
              value={localFilters.rating}
              onValueChange={(value) => {
                if (value === 'N/A') {
                  setLocalFilters((prev) => ({ ...prev, rating: value, excludeNARatings: false }));
                } else {
                  updateFilter('rating', value);
                }
              }}
            >
              <SelectTrigger id="rating" className="h-9">
                <SelectValue placeholder="All Ratings" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                {ratingThresholds.length > 0 ? (
                  // Customer review 11 Sep (item 2): the configured Grade
                  // Thresholds — label and range — for every role (useRatingBands).
                  ratingThresholds.map((threshold) => (
                    <SelectItem key={threshold.id} value={threshold.grade}>
                      {threshold.display_label || threshold.label || threshold.grade}
                      <span className="ml-1 text-muted-foreground">({bandRange(threshold)})</span>
                    </SelectItem>
                  ))
                ) : (
                  <>
                    <SelectItem value="A">A (90-100%)</SelectItem>
                    <SelectItem value="B">B (75-90%)</SelectItem>
                    <SelectItem value="C">C (60-75%)</SelectItem>
                    <SelectItem value="D">D (40-60%)</SelectItem>
                    <SelectItem value="F">F (0-40%)</SelectItem>
                  </>
                )}
                <SelectItem value="N/A">N/A (No Data)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Checkbox
            id="excludeNARatings"
            checked={localFilters.excludeNARatings}
            onCheckedChange={(checked) => updateFilter('excludeNARatings', checked as boolean)}
          />
          <Label htmlFor="excludeNARatings" className="text-sm cursor-pointer">
            Exclude N/A Ratings
          </Label>
        </div>
      </div>
    </>
  );
}
